import { TAB_NAME, updateOverlay, updateChartData, updateTitle } from './fns';
import { oakPhenologyAnnualOptions, springFreezeAnnualOptions, dailyMinimumTemperaturesOptions } from './charts';


const info = {
  name: TAB_NAME,
  subTabs: [{
    name: 'White Oak Freeze Damage'
  },{
    name: 'Red Oak Freeze Damage'
  }],
  chartInfo: {
    chartSelectorId: 'springFreezeDamageChartSelector',
    charts: [{
      name: 'Daily Minimum Temperatures',
      showIfSubTab: ['White Oak Freeze Damage', 'Red Oak Freeze Damage'],
      func: (chartData, address, tabInfoContextState) => dailyMinimumTemperaturesOptions(chartData, address, tabInfoContextState)
    },{
      name: 'Last Spring Freeze and Phenology Timing',
      showIfSubTab: ['White Oak Freeze Damage', 'Red Oak Freeze Damage'],
      func: (chartData, address, _, selectedSubTab) => oakPhenologyAnnualOptions(chartData, address, selectedSubTab.split(' ').slice(0,2).join(' '))
    },{
      name: 'Damaging Spring Freeze Events',
      showIfSubTab: ['White Oak Freeze Damage', 'Red Oak Freeze Damage'],
      func: (chartData, address, _, selectedSubTab) => springFreezeAnnualOptions(chartData, address, selectedSubTab.split(' ').slice(0,2).join(' '))
    }],
  },
  onChange: [{
    whenXChanges: ['selectedSubTab', 'ofdOverlaySelector', 'biomassThresholdSelector'],
    handleChange: updateOverlay,
    showLoading: true
  },{
    whenXChanges: ['rawChartData', 'selectedSubTab', 'oakFreezeDamageTemp'],
    handleChange: updateChartData,
    showLoading: true
  }],
  mapInfo: {
    overlayKeySelectorId: (selectedSubTab) => selectedSubTab.split(' ')[1] === 'Oak' ? 'ofdOverlaySelector' : 'departureSelector',
    regionsSelectorId: 'regionSelector'
  },
  optionsPanel: {
    map: [{
      component: 'listSelector',
      id: 'spring-freeze-damage-regions',
      useSharedState: 'regionSelector',
      props: {
        label: () => 'Select a Region'
      }
    },{
      component: 'radioSelector',
      id: 'ofd-overlay',
      useSharedState: 'ofdOverlaySelector',
      showIfSubTab: ['White Oak Freeze Damage', 'Red Oak Freeze Damage']
    },{
      component: 'thresholdSelector',
      id: 'biomass-threshold-selector',
      useSharedState: 'biomassThresholdSelector',
      props: {
        columnHeading: 'Biomass Threshold',
        minNum: 1,
        maxValue: Infinity,
        step: 0.5,
        colors: ['black'],
        label: () => 'Change Biomass Threshold',
        showReset: true,
        unit: '(tons/acre)',
        automaticUpdateOnEdit: false,
        precision: 1
      }
    }],
    chart: [{
      component: 'listSelector',
      id: 'spring-freeze-damage-charts',
      useSharedState: 'springFreezeDamageChartSelector',
      props: {
        listTypeSingular: 'Chart',
        listTypePlural: 'Charts',
        showAll: false,
        showNone: false,
        label: () => 'Select Indicators',
        groupSx: { gap: '6px' }
      }
    },{
      component: 'thresholdSelector',
      id: 'oak-freeze-damage-temperature-threshold-selector',
      showIfSubTab: ['White Oak Freeze Damage', 'Red Oak Freeze Damage'],
      useSharedState: 'oakFreezeDamageTemp'
    },{
      component: 'chartYearSelector',
      id: 'spring-freeze-chart-year-selector',
      useSharedState: 'springFreezeChartYearSelector',
    }]
  },
  componentFns: {
    title: updateTitle,
    aboveChartsTextBlock: ({ selectedSubTab }) => 'above-tool/' + selectedSubTab.toLowerCase().split(' ').join('-') + '.md',
    aboveMapsTextBlock: ({ selectedSubTab }) => 'above-tool/' + selectedSubTab.toLowerCase().split(' ').join('-') + '.md',
    mapTooltipFormatter: (value, { selectedSubTab, getSelectorValueByIdFromTabInformation }) => {
      if (selectedSubTab.split(' ')[1] === 'Oak') {
        const comparison = getSelectorValueByIdFromTabInformation('ofdOverlaySelector');
        if (comparison === 'Current Year') {
          return value > 0 ? 'Damage Likely' : 'No damaging events';
        } else {
          const roundedValue = Math.round(value);
          return `${roundedValue} years in past 30 with damaging events`;
        }
      } else {
        const comparison = getSelectorValueByIdFromTabInformation('departureSelector');
        if (comparison === 'Current Year') {
          return value < 1 ? 'High Risk' : 'Low Risk';
        } else {
          const roundedValue = Math.round(value * 10) / 10;
          const units = comparison.includes('(Days)') ? ' days' : '%';
          return roundedValue + units;
        }
      }
    } 
  },
  textPanelInfo: [{
    name: 'Introduction',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/connection-to-climate.md'
  },{
    name: 'Spring Freeze Damage Indicator Maps & Interpretation',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/indicator-maps-and-interpretation.md'
  },{
    name: 'Spring Freeze Damage Indicator Series & Interpretation',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Spring Freeze Damage',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['White Oak Freeze Damage'],
    markdownFilename: 'below-tool/white-oak-freeze-damage/additional-resources.md'
  },{

    name: 'Introduction',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/introduction.md'
  },{
    name: 'Connection to Climate',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/connection-to-climate.md'
  },{
    name: 'Spring Freeze Damage Indicator Maps & Interpretation',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/indicator-maps-and-interpretation.md'
  },{
    name: 'Spring Freeze Damage Indicator Series & Interpretation',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/indicator-series-and-interpretation.md'
  },{
    name: 'Management Strategies for Spring Freeze Damage',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/management-strategies.md'
  },{
    name: 'About the Data',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/about-the-data.md'
  },{
    name: 'Additional Resources',
    showIfSubTab: ['Red Oak Freeze Damage'],
    markdownFilename: 'below-tool/red-oak-freeze-damage/additional-resources.md'
  }]
};
export default info;

export const springFreezeDamageSharedStateDefinitions = {
  'springFreezeDamageChartSelector': {
    options: info.chartInfo.charts,
    initValue: info.chartInfo.charts
  },
  'ofdOverlaySelector': {
    initValue: 'Current Year',
    props: {
      options: ['Current Year', 'Last 30 Years'],
      label: () => 'Time Frame',
      title: 'Time Frame'
    }
  },
  'springFreezeChartYearSelector': {
    initValue: '',
    props: {
      label: 'Change Daily Minimum Temperatures Chart Year'
    }
  },
  'oakFreezeDamageTemp': {
    onNavChange: () => ([31]),
    initValue: () => ([31]),
    props: {
      columnHeading: 'Freeze Event Temperature',
      minNum: 1,
      maxNum: 1,
      minValue: -200,
      maxValue: 200,
      initValue: 31,
      resetValue: 31,
      step: 1,
      colors: ['black'],
      label: () => ['Change Temperature Threshold'],
      showReset: true,
      showHeader: false,
      endAdornment: '°F'
    }
  }
};